// src/pages/HomePage.js
import React, { useState, useEffect, useRef } from 'react';
import SupplementFormFilter from '../components/Filters/SupplementFormFilter';
import DietaryRestrictionsFilter from '../components/Filters/DietaryRestrictionsFilter';
import HealthGoalsFilter from '../components/Filters/HealthGoalsFilter';
import IngredientsFilter from '../components/Filters/IngredientsFilter';
import { supplementForms as allSupplementForms } from '../data/filterOptions';
import FloatingSearchButton from '../components/Common/FloatingSearchButton';
import SelectedFiltersDisplay from '../components/Summary/SelectedFiltersDisplay';
import './HomePage.css'; // <-- IMPORT THE CSS FILE
import SupplementResultCard from '../components/Results/SupplementResultCard';
import './ResultsDisplay.css';


const HomePage = () => {
    // State for filters (This part remains the same)
    const initialSupplementForms = {};
    allSupplementForms.forEach(form => {
        initialSupplementForms[form] = true;
    });
    const [selectedSupplementForms, setSelectedSupplementForms] = useState(initialSupplementForms);
    const [selectedDietaryRestrictions, setSelectedDietaryRestrictions] = useState({});
    const [selectedHealthGoals, setSelectedHealthGoals] = useState([]);
    const [selectedIngredients, setSelectedIngredients] = useState([]);

    // === NEW: State for search results, loading, and errors ===
    const [searchResults, setSearchResults] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [hasSearched, setHasSearched] = useState(false); // To know when to show "No results"

    // === NEW: Ref for scrolling to results ===
    const resultsRef = useRef(null);

    // The remove handlers remain the same...
    const handleRemoveForm = (formToRemove) => {
        setSelectedSupplementForms(prev => ({ ...prev, [formToRemove]: false }));
    };
    const handleRemoveDietary = (dietaryKeyToRemove) => {
        setSelectedDietaryRestrictions(prev => {
            const newRestrictions = { ...prev };
            delete newRestrictions[dietaryKeyToRemove];
            return newRestrictions;
        });
    };
    const handleRemoveGoal = (goalToRemove) => {
        setSelectedHealthGoals(prev => prev.filter(g => g !== goalToRemove));
    };
    const handleRemoveIngredient = (ingredientToRemove) => {
        setSelectedIngredients(prev => prev.filter(i => i !== ingredientToRemove));
    };
    
    // === NEW: Function to scroll to results ===
    const scrollToResults = () => {
        if (resultsRef.current) {
            // Calculate offset to account for any fixed headers
            const yOffset = -20; // Adjust this value if you have a fixed header
            const element = resultsRef.current;
            const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
            
            window.scrollTo({ top: y, behavior: 'smooth' });
        }
    };

    // === UPDATED: The search handler with auto-scroll ===
    const handleSearch = async () => {
        setIsLoading(true);
        setError(null);
        setHasSearched(true); // Mark that a search has been attempted

        // Scroll to results immediately when search starts
        // This ensures users see the loading state
        scrollToResults();
        
        // === THIS IS THE CORE OF THE QUERY CONSTRUCTION ===
        // Assemble the four pieces of state into a single, clean JavaScript object named searchFilters
        const searchFilters = {
            forms: selectedSupplementForms,
            dietary: selectedDietaryRestrictions,
            goals: selectedHealthGoals,
            ingredients: selectedIngredients
        };

        try {
            const response = await fetch('https://api.discoversupps.com/search', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(searchFilters),
            });

            if (!response.ok) {
                // Try to get a more specific error from the server, or use a default one
                const errorData = await response.json().catch(() => null);
                throw new Error(errorData?.message || 'The server returned an error.');
            }

            const data = await response.json();
            setSearchResults(data);

        } catch (err) {
            setError(err.message || 'Failed to fetch results. Please check your connection.');
            setSearchResults([]); // Clear previous results on error
        } finally {
            setIsLoading(false);
        }
    };

    // The useEffect hooks for logging (optional, but good for debugging)
    useEffect(() => {
        console.log("Selected Forms Updated:", selectedSupplementForms);
    }, [selectedSupplementForms]);

    useEffect(() => {
        console.log("Selected Dietary Updated:", selectedDietaryRestrictions);
    }, [selectedDietaryRestrictions]);

    useEffect(() => {
        console.log("Selected Goals Updated:", selectedHealthGoals);
    }, [selectedHealthGoals]);

    useEffect(() => {
        console.log("Selected Ingredients Updated:", selectedIngredients);
    }, [selectedIngredients]);

    return (
        <div>
            {/* === REACT 19 SEO TAGS === */}
            <title>DiscoverSupps - Find Amazon Supplements by Ingredients, Health Goals, and More</title>
            <meta
                name="description"
                content="Quickly find Amazon supplements. Filter by ingredients, health goals, dietary needs, and more. DiscoverSupps helps you search smarter."
            />

            {/* === ADD OPEN GRAPH TAGS HERE === */}
            <meta property="og:title" content="DiscoverSupps - Amazon Supplement Finder" />
            <meta property="og:description" content="Find the perfect supplement on Amazon. Filter by health goals, ingredients, dietary needs and more." />
            <meta property="og:type" content="website" />
            <meta property="og:url" content="https://discoversupps.com" />
            <meta property="og:image" content="https://discoversupps.com/logo512.png" />
        
            {/* === ADD STRUCTURED DATA HERE === */}
            <script type="application/ld+json">
                {JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "WebApplication", 
                    "name": "DiscoverSupps",
                    "description": "Find Amazon supplements by ingredients, health goals, and dietary needs",
                    "url": "https://discoversupps.com",
                    "applicationCategory": "HealthApplication",
                    "operatingSystem": "All"
                })}
            </script>

            {/* === END OF SEO TAGS === */}
                
            <link rel="preconnect" href="https://m.media-amazon.com" />
            
            <h1 style={{ textAlign: 'center', marginBottom: '20px', fontSize: '2.25rem' }}>Find Amazon Supplements by Ingredients, Health Goals, and More.</h1>

            <SelectedFiltersDisplay
                selectedForms={selectedSupplementForms}
                selectedDietary={selectedDietaryRestrictions}
                selectedGoals={selectedHealthGoals}
                selectedIngredients={selectedIngredients}
                onRemoveForm={handleRemoveForm}
                onRemoveDietary={handleRemoveDietary}
                onRemoveGoal={handleRemoveGoal}
                onRemoveIngredient={handleRemoveIngredient}
            />

            <div className="top-filters-row">
                <div className="filter-column-1">
                    <SupplementFormFilter
                        selectedForms={selectedSupplementForms}
                        onChange={setSelectedSupplementForms}
                    />
                </div>
                <div className="filter-column-2">
                    <DietaryRestrictionsFilter
                        selectedRestrictions={selectedDietaryRestrictions}
                        onChange={setSelectedDietaryRestrictions}
                    />
                </div>
            </div>

            <HealthGoalsFilter
                selectedGoals={selectedHealthGoals}
                onChange={setSelectedHealthGoals}
            />

            <IngredientsFilter
                selectedIngredients={selectedIngredients}
                onChange={setSelectedIngredients}
            />

            <FloatingSearchButton onClick={handleSearch} />
            
            {/* === UPDATED: Added ref to results container === */}
            <div className="results-container" ref={resultsRef}>
                {isLoading && <p className="status-message">Loading...</p>}
                
                {error && <p className="status-message" style={{ color: '#E53E3E' }}>Error: {error}</p>}

                {!isLoading && !error && hasSearched && searchResults.length === 0 && (
                <p className="status-message">No supplements found matching your criteria.</p>
                )}

                {searchResults.length > 0 && (
                <>
                    <div className="results-grid">
                    {searchResults.map((supplement) => (
                        <SupplementResultCard key={supplement.primaryASIN} supplement={supplement} />
                    ))}
                    </div>
                    <div className="results-disclosure">
                    <p>DiscoverSupps is an Amazon Associate and gets commissions from qualifying purchases.</p>
                    </div>
                </>
                )}
            </div>
        </div>
    );
};

export default HomePage;
